/**
 * Wire-mapping for certification campaigns + attestations (OIDCPAM Gate-6
 * Phase 6b — GovernanceCampaign.tsx).
 *
 * `AdminListCampaignsResp.campaigns` and `AdminListAttestationsResp
 * .attestations` are both typed `Array<Record<string, unknown>>` in the
 * generated client (gen/client/react/client.ts) — the Go handlers
 * (AdminGovernanceListCampaigns / AdminGovernanceListAttestations in
 * admin_governance_server.go) marshal `[]*CertificationCampaign` /
 * `[]*Attestation` directly, bypassing the genapi response-schema encoder,
 * so the generator could not infer a concrete response type. The wire JSON
 * uses snake_case json tags (see pam_certification.go's
 * CertificationCampaign struct: id, tenant_id, name, status, due_at,
 * created_at, updated_at, ...; and pam_attestation.go's Attestation struct:
 * id, tenant_id, campaign_id, discovery_id, reviewer_id, decision,
 * justification, decided_at), and the generated client's fromWire()
 * converts EVERY response key snake_case->camelCase before resolving —
 * including nested Record<string, unknown> array items, since the
 * conversion is a blind recursive key-walk, not schema-aware. So the REAL
 * runtime shape at this boundary is WireCertificationCampaign /
 * WireAttestation below (camelCase), NOT the hand-written snake_case
 * CertificationCampaign / Attestation types in ../api/types.ts.
 *
 * LATENT BUGS FIXED (Gate-6 shared-doc bug class): the pre-migration page
 * cast both list responses straight to the snake_case types via
 * `as unknown as` and read fields like c.due_at, record.subject_id,
 * record.resource_id, record.grant_kind, and — most importantly —
 * `a.discovery_id` when matching an attestation to its discovered-access
 * record (`attestations.find((a) => a.discovery_id === record.id)`). Every
 * one of those keys is `undefined` on the actual camelCase runtime object,
 * so: (1) the campaign table's Due column rendered blank; (2) the
 * attestation panel's Subject/Resource/Grant-kind columns rendered blank;
 * and (3) — a FUNCTIONAL bug, not just a blank field — the attestation
 * lookup NEVER matched, so every record always showed "pending" status
 * even after a decision had been submitted and persisted. This mapping maps
 * camelCase -> camelCase field-for-field onto @devnw/cyber-iam's
 * iam/types#CertificationCampaign / #Attestation shapes, pre-formatting
 * dueAt/createdAt/updatedAt/decidedAt as display strings (the library never
 * parses dates itself).
 */
import type {
  CertificationCampaign,
  Attestation,
  AttestationDecision,
  CampaignStatus,
} from '@devnw/cyber-iam/iam/types'

/** The real (camelCase) runtime shape of one AdminListCampaignsResp item. */
export interface WireCertificationCampaign {
  id: string
  name: string
  status: CampaignStatus
  dueAt: string
  createdAt?: string
  updatedAt?: string
}

/** The real (camelCase) runtime shape of one AdminListAttestationsResp item. */
export interface WireAttestation {
  id: string
  campaignId: string
  discoveryId: string
  reviewerId: string
  decision: AttestationDecision
  justification?: string
  decidedAt: string
}

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtDate(iso?: string): string | undefined {
  return iso ? new Date(iso).toLocaleDateString() : undefined
}

function fmtDateTime(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : '—'
}

export function toCertificationCampaign(
  w: WireCertificationCampaign
): CertificationCampaign {
  return {
    id: w.id,
    name: w.name,
    status: w.status,
    dueAt: fmtDate(w.dueAt) ?? '—',
    createdAt: fmtDate(w.createdAt),
    updatedAt: fmtDate(w.updatedAt),
  }
}

export function toAttestation(w: WireAttestation): Attestation {
  return {
    id: w.id,
    campaignId: w.campaignId,
    discoveryId: w.discoveryId,
    reviewerId: w.reviewerId,
    decision: w.decision,
    justification: w.justification,
    decidedAt: fmtDateTime(w.decidedAt),
  }
}
